import React from 'react';
import { FlatList, TouchableOpacity } from 'react-native';
import { Card, H5, XStack, YStack, SizableText } from 'tamagui';
import { MaterialIcons, FontAwesome } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { formatCurrency, formatDateTime, formatPaymentMethod, formatCardBrand } from '@utils/formatters';

interface Transaction {
  id: string;
  amount: number;
  currency: string;
  status: string;
  created: string;
  description: string;
  customer: string;
  receiptUrl: string;
  paymentMethod: {
    type: string;
    brand?: string;
    last4?: string;
  };
}

interface RecentTransactionsProps {
  transactions: Transaction[];
  isLoading?: boolean;
  onTransactionPress?: (transaction: Transaction) => void;
}

const RecentTransactions: React.FC<RecentTransactionsProps> = ({
  transactions,
  isLoading,
  onTransactionPress
}) => {
  const theme = themeSelector();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'succeeded': return '#10b981';
      case 'failed': return '#ef4444';
      case 'pending': return '#f59e0b';
      case 'canceled': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'succeeded': return 'Success';
      case 'failed': return 'Failed';
      case 'pending': return 'Pending';
      case 'canceled': return 'Canceled';
      default: return status;
    }
  };

  const getPaymentIcon = (type: string, brand?: string) => {
    if (type === 'card') {
      switch (brand?.toLowerCase()) {
        case 'visa': return 'cc-visa';
        case 'mastercard': return 'cc-mastercard';
        case 'amex': return 'cc-amex';
        case 'discover': return 'cc-discover';
        default: return 'credit-card';
      }
    }
    switch (type) {
      case 'bank_transfer': return 'bank';
      case 'sepa_debit': return 'bank';
      case 'ideal': return 'bank';
      case 'klarna': return 'credit-card';
      case 'alipay': return 'mobile';
      case 'wechat_pay': return 'mobile';
      default: return 'credit-card';
    }
  };

  const renderTransaction = ({ item }: { item: Transaction }) => (
    <TouchableOpacity
      onPress={() => onTransactionPress && onTransactionPress(item)}
      activeOpacity={0.7}
    >
      <Card
        padding="$3"
        marginBottom="$2"
        backgroundColor={theme ? "#f9fafb" : "#374151"}
        borderRadius="$3"
        borderWidth={1}
        borderColor={theme ? "#e5e7eb" : "#4b5563"}
      >
        <XStack space="$3" alignItems="center">
          {/* Payment Method Icon */}
          <Card
            padding="$2"
            backgroundColor={theme ? "#ffffff" : "#1f2937"}
            borderRadius="$2"
            width={40}
            height={40}
            justifyContent="center"
            alignItems="center"
          >
            <FontAwesome
              name={getPaymentIcon(item.paymentMethod.type, item.paymentMethod.brand)}
              size={16}
              color={theme ? "#374151" : "#e5e7eb"}
            />
          </Card>

          {/* Transaction Details */}
          <YStack flex={1} space="$1">
            <XStack justifyContent="space-between" alignItems="center">
              <SizableText
                size="$4"
                fontWeight="600"
                color={theme ? "#111827" : "#f9fafb"}
              >
                {formatCurrency(item.amount, item.currency.toUpperCase())}
              </SizableText>
              <SizableText
                size="$2"
                backgroundColor={getStatusColor(item.status)}
                color="white"
                paddingHorizontal="$2"
                paddingVertical="$1"
                borderRadius="$2"
                fontSize={10}
                fontWeight="600"
                textAlign="center"
              >
                {getStatusText(item.status)}
              </SizableText>
            </XStack>

            <SizableText
              size="$3"
              color={theme ? "#374151" : "#d1d5db"}
              numberOfLines={1}
            >
              {item.description || 'No description'}
            </SizableText>

            <XStack justifyContent="space-between" alignItems="center">
              <XStack alignItems="center" space="$1">
                <SizableText size="$2" color={theme ? "#6b7280" : "#9ca3af"}>
                  {formatPaymentMethod(item.paymentMethod.type)}
                </SizableText>
                {item.paymentMethod.last4 && (
                  <SizableText size="$2" color={theme ? "#6b7280" : "#9ca3af"}>
                    •••• {item.paymentMethod.last4}
                  </SizableText>
                )}
              </XStack>
              <SizableText size="$2" color={theme ? "#6b7280" : "#9ca3af"}>
                {formatDateTime(item.created)}
              </SizableText>
            </XStack>
          </YStack>

          {/* Arrow */}
          <MaterialIcons
            name="chevron-right"
            size={20}
            color={theme ? "#9ca3af" : "#6b7280"}
          />
        </XStack>
      </Card>
    </TouchableOpacity>
  );

  if (isLoading) {
    return (
      <Card
        padding="$4"
        backgroundColor={theme ? "#ffffff" : "#1e293b"}
        borderRadius={20}
        shadowColor="#000"
        shadowOffset={{ width: 0, height: 12 }}
        shadowOpacity={theme ? 0.08 : 0.25}
        shadowRadius={24}
        elevation={12}
        borderWidth={1}
        borderColor={theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"}
        overflow="hidden"
      >
        <YStack space="$3" alignItems="center" minHeight={200}>
          <H5 color={theme ? "#111827" : "#f9fafb"} fontWeight="bold">
            Recent Transactions
          </H5>
          <SizableText color={theme ? "#6b7280" : "#9ca3af"}>
            Loading transactions...
          </SizableText>
        </YStack>
      </Card>
    );
  }

  return (
    <Card
      padding="$4"
      backgroundColor={theme ? "#ffffff" : "#1e293b"}
      borderRadius={20}
      shadowColor="#000"
      shadowOffset={{ width: 0, height: 12 }}
      shadowOpacity={theme ? 0.08 : 0.25}
      shadowRadius={24}
      elevation={12}
      borderWidth={1}
      borderColor={theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"}
      overflow="hidden"
    >
      <YStack space="$3">
        {/* Header */}
        <XStack flexWrap="wrap" justifyContent="space-between" alignItems="center">
          <H5 color={theme ? "#111827" : "#f9fafb"} fontWeight="bold">
            Recent Transactions
          </H5>
          <SizableText size="$3" color={theme ? "#6b7280" : "#9ca3af"}>
            {transactions.length} transactions
          </SizableText>
        </XStack>

        {/* Transactions List */}
        {transactions.length > 0 ? (
          <FlatList
            data={transactions}
            renderItem={renderTransaction}
            keyExtractor={(item) => item.id}
            showsVerticalScrollIndicator={false}
            style={{ maxHeight: 400 }}
          />
        ) : (
          <YStack alignItems="center" padding="$4" space="$2">
            <MaterialIcons
              name="receipt"
              size={48}
              color={theme ? "#d1d5db" : "#6b7280"}
            />
            <SizableText
              size="$4"
              color={theme ? "#6b7280" : "#9ca3af"}
              textAlign="center"
            >
              No transactions found
            </SizableText>
            <SizableText
              size="$3"
              color={theme ? "#9ca3af" : "#6b7280"}
              textAlign="center"
            >
              Your recent transactions will appear here
            </SizableText>
          </YStack>
        )}
      </YStack>
    </Card>
  );
};

export default RecentTransactions;
